import { createContext, useContext, useState } from 'react';

import PropTypes from 'prop-types';

import {
	useCreateEmailCampaign,
	useUpdateEmailCampaign,
	useDeleteEmailCampaign,
} from '../models/emailCampaigns/mutations';
import { useEmailCampaigns as useEmailCampaignsQuery, useCampaignStatusEnum } from '../models/emailCampaigns/queries';

const EmailCampaignsContext = createContext(null);

export const useEmailCampaignsContext = () => {
	const context = useContext(EmailCampaignsContext);
	if (!context) {
		throw new Error('useEmailCampaignsContext must be used within EmailCampaignsProvider');
	}
	return context;
};

export const EmailCampaignsProvider = ({ children }) => {
	// UI state
	const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
	const [selectedCampaign, setSelectedCampaign] = useState(null);

	// React Query hooks
	const {
		data: emailCampaigns = [],
		isLoading: loadingCampaigns,
		refetch: refetchCampaigns,
	} = useEmailCampaignsQuery();

	const { data: campaignStatusOptions = [], isLoading: loadingStatuses } = useCampaignStatusEnum();

	// Mutations
	const createCampaignMutation = useCreateEmailCampaign();
	const updateCampaignMutation = useUpdateEmailCampaign();
	const deleteCampaignMutation = useDeleteEmailCampaign();

	// Action handlers
	const handleCreateCampaign = async ({ name, campaignType = 'template' }) => {
		const result = await createCampaignMutation.mutateAsync({ name, campaignType });
		if (result.data) {
			return result.data;
		}
		throw new Error('Kampagne konnte nicht erstellt werden');
	};

	const handleUpdateCampaign = async ({ id, updates, prevStatus }) => {
		await updateCampaignMutation.mutateAsync({ id, updates, prevStatus });
	};

	const handleDeleteCampaign = async (id) => {
		await deleteCampaignMutation.mutateAsync(id);
	};

	const contextValue = {
		// Data
		emailCampaigns,
		campaignStatusOptions,

		// UI state
		showCreateCampaignModal,
		setShowCreateCampaignModal,
		selectedCampaign,
		setSelectedCampaign,

		// Loading states
		loadingCampaigns,
		loadingStatuses,

		// Actions
		handleCreateCampaign,
		handleUpdateCampaign,
		handleDeleteCampaign,

		// Refetch functions
		refetchCampaigns,
	};

	return <EmailCampaignsContext.Provider value={contextValue}>{children}</EmailCampaignsContext.Provider>;
};

EmailCampaignsProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
